import { create } from "zustand";

import type { SongShareData } from "../steps/DetailsAndSharesStep";
import type {
  SpotifyAlbumInfoResponse,
  SpotifyTrackItem,
} from "../types/spotify";

// Using SpotifyAlbumInfoResponse as the type for album data
type SpotifyAlbumData = SpotifyAlbumInfoResponse;

// Selected track state for the workflow
export type SelectedTrack = SpotifyTrackItem & {
  isSelected: boolean;
  isRemix?: boolean;
  containsSamples?: boolean;
};

// Define the store state
type AlbumStoreState = {
  albumData: SpotifyAlbumData | null;
  tracks: SelectedTrack[];
  apiToken: string | null;
  isLoading: boolean;
  error: string | null;
  songShares: Record<string, SongShareData>;
  currentSongIndex: number;
};

// Define the store actions
type AlbumStoreActions = {
  setAlbumData: (albumData: SpotifyAlbumData | null) => void;
  setTracks: (tracks: SpotifyTrackItem[]) => void;
  setApiToken: (token: string | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  toggleTrackSelection: (trackId: string) => void;
  selectAllTracks: (selected: boolean) => void;
  toggleTrackRemix: (trackId: string) => void;
  toggleTrackContainsSamples: (trackId: string) => void;
  setAllTracksRemix: (isRemix: boolean) => void;
  setAllTracksContainsSamples: (containsSamples: boolean) => void;
  getSelectedTracks: () => SelectedTrack[];
  setSongShares: (songShares: Record<string, SongShareData>) => void;
  setCurrentSongIndex: (index: number) => void;
  clearState: () => void;
};

// Create the store
const useAlbumStore = create<AlbumStoreState & AlbumStoreActions>((set, get) => ({
  albumData: null,
  tracks: [],
  apiToken: null,
  isLoading: false,
  error: null,
  songShares: {},
  currentSongIndex: 0,

  setAlbumData: albumData => set({ albumData }),

  setTracks: tracks => set({
    tracks: tracks.map(track => ({
      ...track,
      isSelected: false,
      isRemix: false,
      containsSamples: false,
    })),
  }),

  setApiToken: apiToken => set({ apiToken }),

  setIsLoading: isLoading => set({ isLoading }),

  setError: error => set({ error }),

  toggleTrackSelection: trackId => set((state) => {
    const trackIndex = state.tracks.findIndex(track => track.id === trackId);
    if (trackIndex === -1)
      return state;

    const newTracks = [...state.tracks];
    newTracks[trackIndex] = {
      ...newTracks[trackIndex],
      isSelected: !newTracks[trackIndex].isSelected,
    };
    return { tracks: newTracks };
  }),

  selectAllTracks: selected => set(state => ({
    tracks: state.tracks.map(track => ({ ...track, isSelected: selected })),
  })),

  toggleTrackRemix: trackId => set((state) => {
    const trackIndex = state.tracks.findIndex(track => track.id === trackId);
    if (trackIndex === -1)
      return state;

    const newTracks = [...state.tracks];
    newTracks[trackIndex] = {
      ...newTracks[trackIndex],
      isRemix: !newTracks[trackIndex].isRemix,
    };
    return { tracks: newTracks };
  }),

  toggleTrackContainsSamples: trackId => set((state) => {
    const trackIndex = state.tracks.findIndex(track => track.id === trackId);
    if (trackIndex === -1)
      return state;

    const newTracks = [...state.tracks];
    newTracks[trackIndex] = {
      ...newTracks[trackIndex],
      containsSamples: !newTracks[trackIndex].containsSamples,
    };
    return { tracks: newTracks };
  }),

  setAllTracksRemix: isRemix => set(state => ({
    tracks: state.tracks.map(track => ({ ...track, isRemix })),
  })),

  setAllTracksContainsSamples: containsSamples => set(state => ({
    tracks: state.tracks.map(track => ({ ...track, containsSamples })),
  })),

  getSelectedTracks: () => {
    return get().tracks.filter(track => track.isSelected);
  },

  setSongShares: songShares => set({ songShares }),

  setCurrentSongIndex: currentSongIndex => set({ currentSongIndex }),

  clearState: () => set({
    albumData: null,
    tracks: [],
    apiToken: null,
    isLoading: false,
    error: null,
    songShares: {},
    currentSongIndex: 0,
  }),
}));

export default useAlbumStore;
